import { tryJson } from '../../utils/commonUtil.js';

function parseJsonField(value, defaultVal) {
  if (value == null) return defaultVal;
  if (typeof value !== 'string') return value;
  return tryJson(value, defaultVal);
}

function resolveApiOrderId(order) {
  return order.id ?? null;
}

/**
 * Map Shopify snake_case (and JSON string fields) into the existing owner Orders UI shape.
 */
export function normalizeEventOrder(order) {
  if (!order) return null;

  const apiOrderId = resolveApiOrderId(order);
  if (apiOrderId == null) return null;

  return {
    id: String(apiOrderId),
    name: order.name ?? null,
    totalPrice: Number(order.total_price ?? order.totalPrice ?? 0),
    subtotalPrice: Number(order.subtotal_price ?? order.subtotalPrice ?? 0),
    taxPrice: Number(order.tax_price ?? order.taxPrice ?? 0),
    totalDiscounts: Number(order.total_discounts ?? order.totalDiscounts ?? 0),
    taxesIncluded: order.taxes_included ?? order.taxesIncluded ?? false,
    paymentStatus: order.payment_status ?? order.paymentStatus ?? '',
    fulfillmentStatus: order.fulfillment_status ?? order.fulfillmentStatus ?? '',
    currency: order.currency ?? '',
    createdAt: order.created_at ?? order.createdAt ?? null,
    updatedAt: order.updated_at ?? order.updatedAt ?? null,
    eventCode: order.event_code ?? order.eventCode ?? null,
    ecommerceProvider: order.ecommerce_provider ?? order.ecommerceProvider ?? null,
    lineItems: parseJsonField(order.line_items_json ?? order.lineItems, []),
    tags: getOrderTags(order),
    customerInfo: parseJsonField(order.customer_info_json ?? order.customerInfo, {}),
    shippingAddress: parseJsonField(order.shipping_address ?? order.shippingAddress, null),
    pickupAddress: parseJsonField(order.pickup_address ?? order.pickupAddress, null),
    discounts: parseJsonField(order.discounts_json ?? order.discounts, []),
    taxLines: parseJsonField(order.tax_lines_json ?? order.tax_lines ?? order.taxLines, []),
  };
}

export function normalizeEventOrders(orders) {
  if (!Array.isArray(orders)) {
    return { orders: [], skippedCount: 0 };
  }

  const normalizedOrders = [];
  let skippedCount = 0;

  orders.forEach((order) => {
    const normalizedOrder = normalizeEventOrder(order);
    if (normalizedOrder) {
      normalizedOrders.push(normalizedOrder);
      return;
    }
    skippedCount += 1;
  });

  return { orders: normalizedOrders, skippedCount };
}

const FULFILLED_STATUSES = new Set(['fulfilled', 'completed']);
const FULFILLABLE_STATUSES = new Set(['unfulfilled', 'pending', '']);

export function normalizeFulfillmentStatus(fulfillmentStatus) {
  return String(fulfillmentStatus || '')
    .toLowerCase()
    .trim();
}

export function isOrderFulfilled(fulfillmentStatus) {
  return FULFILLED_STATUSES.has(normalizeFulfillmentStatus(fulfillmentStatus));
}

const PICKUP_PRODUCT_PURPOSES = new Set(['printable']);
const MOBILE_ITEM_TAG = 'mobile';

export function getLineItemVariantList(lineItem) {
  const attributes = lineItem?.customAttributes || lineItem?.properties;
  const variantAttr = attributes?.find((attr) => attr.key === '_variantArr' || attr.name === '_variantArr');
  const parsed = parseJsonField(variantAttr?.value, []);
  return Array.isArray(parsed) ? parsed : [];
}

function getVariantProductPurpose(variant) {
  return variant?.productPurpose ?? variant?.product_purpose;
}

export function hasPickupProductPurpose(lineItems) {
  if (!Array.isArray(lineItems)) {
    return false;
  }

  return lineItems.some((lineItem) =>
    getLineItemVariantList(lineItem).some((variant) => PICKUP_PRODUCT_PURPOSES.has(getVariantProductPurpose(variant)))
  );
}

function normalizeOrderTags(tags) {
  if (Array.isArray(tags)) {
    return tags;
  }
  if (typeof tags === 'string') {
    return tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }
  return [];
}

function getOrderTags(order) {
  const rawTags = order.tags_json ?? order.tags;
  if (Array.isArray(rawTags)) {
    return rawTags;
  }
  const parsedTags = parseJsonField(rawTags, rawTags);
  return normalizeOrderTags(parsedTags);
}

function hasMobileItemTag(order) {
  return getOrderTags(order).some((tag) => String(tag).trim().toLowerCase() === MOBILE_ITEM_TAG);
}

function getShippingAddress(order) {
  return order?.shippingAddress ?? order?.shipping_address ?? null;
}

function getPickupAddress(order) {
  return order?.pickupAddress ?? order?.pickup_address ?? null;
}

const ORDER_ADDRESS_LABELS = {
  shipping: 'Shipping address',
  pickup: 'Pickup address',
  none: 'Shipping/pickup address',
};

export function getOrderAddressDisplay(order) {
  const shippingAddress = getShippingAddress(order);
  if (shippingAddress != null) {
    return { label: ORDER_ADDRESS_LABELS.shipping, address: shippingAddress };
  }

  const pickupAddress = getPickupAddress(order);
  if (pickupAddress != null) {
    return { label: ORDER_ADDRESS_LABELS.pickup, address: pickupAddress };
  }

  return { label: ORDER_ADDRESS_LABELS.none, address: null };
}

export function isPickupOrder(order) {
  if (!order) {
    return false;
  }

  if (!isOrderFulfilled(order.fulfillmentStatus ?? order.fulfillment_status)) {
    return false;
  }

  return getPickupAddress(order) != null && hasMobileItemTag(order);
}

export function isPendingPickupOrder(order) {
  if (!order || isOrderFulfilled(order.fulfillmentStatus ?? order.fulfillment_status)) {
    return false;
  }

  return getPickupAddress(order) != null && hasMobileItemTag(order);
}

export function isFulfillableStatus(fulfillmentStatus) {
  return FULFILLABLE_STATUSES.has(normalizeFulfillmentStatus(fulfillmentStatus));
}

export function matchesFulfillmentFilter(fulfillmentStatus, filterValue) {
  if (filterValue === 'fulfilled') {
    return isOrderFulfilled(fulfillmentStatus);
  }
  if (filterValue === 'unfulfilled') {
    return isFulfillableStatus(fulfillmentStatus);
  }
  if (filterValue === 'other') {
    return !isOrderFulfilled(fulfillmentStatus) && !isFulfillableStatus(fulfillmentStatus);
  }
  return true;
}

export function getFulfillOrderOutcome(result) {
  if (result?.status !== 'ok') {
    return { kind: 'error' };
  }

  const payload = result?.result ?? {};
  if (payload.fulfilled === true) {
    return {
      kind: 'fulfilled',
      fulfillmentStatus: payload.fulfillment_status || payload.fulfillmentStatus || 'fulfilled',
    };
  }

  return { kind: 'not_fulfilled' };
}
